
import { cacheService } from '@/lib/cacheService';
import { fetchSpotPricesFromAPI, fetchHistoryFromAPI } from './api';
import { MetalPrice, TimePeriod } from './types';

const CURRENT_PRICES_CACHE_KEY = 'current_metal_prices';
const CURRENT_PRICES_CACHE_TTL = 60 * 1000; // 1 minute

const HISTORY_CACHE_TTL = 4 * 60 * 60 * 1000; // 4 hours

export const fetchCurrentPrices = async (): Promise<{gold: MetalPrice, silver: MetalPrice}> => {
  const cached = cacheService.get<{gold: MetalPrice, silver: MetalPrice}>(CURRENT_PRICES_CACHE_KEY, CURRENT_PRICES_CACHE_TTL);
  if (cached) {
    return cached.data;
  }

  const pricesData = await fetchSpotPricesFromAPI();
  if (pricesData && 'gold' in pricesData && 'silver' in pricesData) {
    const prices = { gold: pricesData.gold, silver: pricesData.silver };
    cacheService.set(CURRENT_PRICES_CACHE_KEY, prices, { expiration: CURRENT_PRICES_CACHE_TTL });
    return prices;
  }
  throw new Error('Invalid data structure for current prices from API');
};

export const fetchPriceHistory = async (
  period: TimePeriod
): Promise<{goldHistory: Partial<MetalPrice>[], silverHistory: Partial<MetalPrice>[]}> => {
  const cacheKey = `history_${period}`;
  const cached = cacheService.get<{goldHistory: Partial<MetalPrice>[], silverHistory: Partial<MetalPrice>[]}>(cacheKey, HISTORY_CACHE_TTL);
  if (cached) {
    return cached.data;
  }

  const historyData = await fetchHistoryFromAPI(period);
  if (historyData && 'goldHistory' in historyData && 'silverHistory' in historyData) {
    const history = { goldHistory: historyData.goldHistory, silverHistory: historyData.silverHistory };
    cacheService.set(cacheKey, history, { expiration: HISTORY_CACHE_TTL });
    return history;
  }
  throw new Error(`Invalid data structure for price history for period: ${period}`);
};

export * from './types';
